import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, CardContent, Typography, TextField, MenuItem,
  Button, Box, IconButton, Alert,
  RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import CalculateIcon from '@mui/icons-material/Calculate';
import { scenarios } from '../scenarios';
import { defaultCustomerConfig } from '../theme/theme';
import DebugPanel from './DebugPanel';
import YearPickerAccordion from './YearPickerAccordion';

const API_BASE = process.env.REACT_APP_API_URL || '';

function isoToDisplay(iso) {
  if (!iso || iso.length < 10) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function displayToIso(display) {
  if (!display || display.length < 10) return '';
  const parts = display.split('/');
  if (parts.length !== 3) return '';
  const [d, m, y] = parts;
  if (!d || !m || !y || y.length !== 4) return '';
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

function maskDate(raw, inputType = '') {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const deleting = inputType.startsWith('delete');
  if (digits.length < 2) return digits;
  if (digits.length === 2) return deleting ? digits : `${digits}/`;
  if (digits.length < 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  if (digits.length === 4) return deleting ? `${digits.slice(0, 2)}/${digits.slice(2)}` : `${digits.slice(0, 2)}/${digits.slice(2)}/`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

const formatNumber = (val) => val ? Number(val).toLocaleString('en-US') : '';
const parseNumber  = (val) => Number(val.replace(/,/g, ''));

const MONTHS              = Array.from({ length: 12 }, (_, i) => i + 1);
const CAPITALIZATION_RATE_OPTS  = [5, 10, 15, 20, 25];
const CAPITALIZATION_YEARS_OPTS = [1, 2, 3, 4, 5];
const ADVANCE_MONTH_OPTS      = [0, 1, 2, 3];
const INSURANCE_END_AGE_OPTS  = [60, 62, 64, 67];
const CURRENT_YEAR        = new Date().getFullYear();
const CURRENT_MONTH       = new Date().getMonth() + 1;
const MAX_RETIREMENT_YEAR = CURRENT_MONTH === 12 ? CURRENT_YEAR + 1 : CURRENT_YEAR;
const MAX_RETIREMENT_MONTH = CURRENT_MONTH === 12 ? 1 : CURRENT_MONTH + 1;

const defaultForm = {
  fundType:            'comprehensive',
  retirementMonth:     Math.min(new Date().getMonth() + 2 > 12 ? 1 : new Date().getMonth() + 2, MAX_RETIREMENT_MONTH),
  retirementYear:      CURRENT_YEAR,
  memberBirthDate:     '',
  memberGender:        '',
  hasSpouse:           true,
  spouseBirthDate:     '',
  spouseGender:        '',
  spousePensionRate:   100,
  savingsAmount:       '',
  securityPeriod:      0,
  advanceMonths:       0,
  capitalizationRate:  0,
  capitalizationYears: 0,
  managementFee:       0.3,
  insuranceEndAge:     67,
  clientName:          '',
  clientId:            '',
  orphans:             [],  // array of { birthdate: '', orphanRate: '' }
};

// field → i18n key mapping (used for both field names and error reasons)
const FIELD_KEY_MAP = {
  savingsAmount:     'savingsAmount',
  memberGender:      'memberGender',
  memberBirthDate:   'memberBirthDate',
  spouseBirthDate:   'spouseBirthDate',
  spouseGender:      'spouseGender',
  spousePensionRate: 'spousePensionRate',
};

// Backend reason (Hebrew) → i18n key
const REASON_KEY_MAP = {
  'שדה חובה חסר':                                              'errorRequired',
  'צבירה חייבת להיות מספר חיובי':                              'errorSavings',
  'גיל העמית/ה בעת הפרישה חייב להיות לפחות 60':               'errorAge',
  'תאריך לידה לא תקין':                                        'errorInvalidDate',
  'תאריך לידה בן/בת זוג לא תקין':                             'errorInvalidDate',
  'דמי ניהול חייבים להיות מספר אי-שלילי':                     'errorMgmtFee',
  'שיעור קצבה בן/בת זוג חייב להיות בין 0 ל-100%':            'errorRateExceeds100',
  'בנוכחות יתומים, שיעור קצבה בן/בת זוג חייב להיות לפחות 30%': 'orphanRateTooLow',
  'שיעור יתום חייב להיות מספר בין 0 ל-100':                   'orphanRateTooHigh',
  'גיל היתום בעת הפרישה חייב להיות פחות מ-21':                'errorOrphanAge',
};

// RadioToggle — looks like ToggleButtonGroup but uses proper <input type="radio"> for a11y.
//
// MUST stay at module scope. Declared inside InputFormV2 it gets a new function identity on
// every render, so React unmounts and remounts the whole radio group instead of updating it.
// The focused <input type="radio"> is destroyed, focus falls back to <body>, and NVDA
// announces the document title on every interaction (A-2-Z audit, row 9 — קורא מסך / מגדר).
//
// No custom onKeyDown here on purpose: a native radio group (inputs sharing one `name`)
// already moves focus and selection together with the arrow keys, and wraps at both ends.
const RadioToggle = ({ name, value, onChange, options, ariaLabel }) => (
  <RadioGroup row name={name} value={value} onChange={e => onChange(e.target.value)}
    aria-label={ariaLabel}
    sx={{ gap: 1, flexWrap: 'nowrap' }}>
    {options.map(({ val, label }) => (
      <FormControlLabel key={val} value={val} label={label}
        control={<Radio size="small" sx={{ color: 'secondary.main', '&.Mui-checked': { color: 'secondary.main' } }} />}
        sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12.5px', fontWeight: 600 } }}
      />
    ))}
  </RadioGroup>
);

// Field label — renders as <label> when htmlFor is provided.
// Also module scope: it renders directly above each RadioToggle, so declaring it inside the
// component churned the same part of the accessibility tree on every render.
const FL = ({ children, htmlFor, inline }) => (
  <Typography component={htmlFor ? 'label' : 'p'} htmlFor={htmlFor} sx={{
    fontSize: '12px', fontWeight: 700, color: '#4a5568',
    textTransform: 'uppercase', letterSpacing: '.4px',
    display: inline ? 'inline' : 'block', mb: inline ? 0 : 0.5,
    cursor: htmlFor ? 'default' : undefined,
  }}>
    {children}
  </Typography>
);

export default function InputFormV2({ onResult, onLoading, config = defaultCustomerConfig, selectedScenario = '', debugMode = false }) {
  const { t } = useTranslation();
  const MIN_RETIREMENT_YEAR = CURRENT_YEAR - (config.retirementYearsBack ?? 7);
  const [form, setForm] = useState(defaultForm);
  const [bannerErrors, setBannerErrors] = useState([]);
  const [bannerWarning, setBannerWarning] = useState(null);
  const [debugResult, setDebugResult] = useState(null);
  const [idWarning, setIdWarning] = useState(false);
  const [dateInputs, setDateInputs] = useState({});  // field → in-progress display string

  const errorBannerRef = useRef(null);
  const cursorPos = useRef(null);
  useLayoutEffect(() => {
    if (cursorPos.current) {
      const el = document.getElementById(cursorPos.current.id);
      if (el) el.setSelectionRange(cursorPos.current.pos, cursorPos.current.pos);
      cursorPos.current = null;
    }
  });
  useEffect(() => {
    if (bannerErrors.length > 0 && errorBannerRef.current) {
      errorBannerRef.current.focus();
    }
  }, [bannerErrors]);

  // MUI v9 sets aria-labelledby on Select combobox divs pointing to the element itself
  // (self-reference), which overrides aria-label and causes screen readers to announce
  // only the selected value instead of the field name. Removing the self-reference
  // allows our aria-label (set via SelectDisplayProps) to take effect.
  useEffect(() => {
    document.querySelectorAll('[role="combobox"]').forEach(el => {
      if (el.id && el.getAttribute('aria-labelledby') === el.id) {
        el.removeAttribute('aria-labelledby');
      }
    });
  });

  const clearErrors = () => { setBannerErrors([]); setBannerWarning(null); };

  // Get display value: use in-progress typed value if present, else convert ISO
  const getDateDisplay = (fieldName, isoValue) =>
    dateInputs[fieldName] !== undefined ? dateInputs[fieldName] : isoToDisplay(isoValue);

  // Handle date typing with auto-mask; updates form ISO when 8 digits complete
  const handleDateChange = (fieldName, e) => {
    clearErrors();
    const rawValue = e.target.value;
    const inputType = e.nativeEvent.inputType ?? '';
    const selStart = e.target.selectionStart ?? rawValue.length;
    const masked = maskDate(rawValue, inputType);
    const digitsBefore = rawValue.slice(0, selStart).replace(/\D/g, '').length;
    let pos = masked.length, count = 0;
    for (let i = 0; i < masked.length; i++) {
      if (count === digitsBefore) { pos = i; break; }
      if (masked[i] !== '/') count++;
    }
    cursorPos.current = { id: `input-${fieldName}`, pos };
    setDateInputs(prev => ({ ...prev, [fieldName]: masked }));
    if (masked.replace(/\D/g, '').length === 8) {
      const iso = displayToIso(masked);
      if (iso) {
        setForm(prev => ({ ...prev, [fieldName]: iso }));
        setDateInputs(prev => { const n = { ...prev }; delete n[fieldName]; return n; });
        cursorPos.current = null;
      }
    } else {
      setForm(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleOrphanDateChange = (idx, e) => {
    clearErrors();
    const rawValue = e.target.value;
    const inputType = e.nativeEvent.inputType ?? '';
    const selStart = e.target.selectionStart ?? rawValue.length;
    const key = `orphan_${idx}`;
    const masked = maskDate(rawValue, inputType);
    const digitsBefore = rawValue.slice(0, selStart).replace(/\D/g, '').length;
    let pos = masked.length, count = 0;
    for (let i = 0; i < masked.length; i++) {
      if (count === digitsBefore) { pos = i; break; }
      if (masked[i] !== '/') count++;
    }
    cursorPos.current = { id: `input-orphan-date-${idx}`, pos };
    setDateInputs(prev => ({ ...prev, [key]: masked }));
    if (masked.replace(/\D/g, '').length === 8) {
      const iso = displayToIso(masked);
      if (iso) {
        setForm(prev => {
          const orphans = prev.orphans.map((o, i) => i === idx ? { ...o, birthdate: iso } : o);
          return { ...prev, orphans };
        });
        setDateInputs(prev => { const n = { ...prev }; delete n[key]; return n; });
        cursorPos.current = null;
      }
    } else {
      setForm(prev => {
        const orphans = prev.orphans.map((o, i) => i === idx ? { ...o, birthdate: '' } : o);
        return { ...prev, orphans };
      });
    }
  };

  const set = (field, value) => {
    clearErrors();
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Apply scenario from parent when selectedScenario changes
  useEffect(() => {
    if (selectedScenario === '' || selectedScenario === null || selectedScenario === undefined) return;
    const scenarioData = scenarios[Number(selectedScenario)].data;
    setForm({ ...defaultForm, ...scenarioData });
    clearErrors();
  }, [selectedScenario]);

  const loadScenario = (scenario) => {
    setForm({ ...defaultForm, ...scenario.data });
    clearErrors();
  };

  const removeOrphan = (idx) => { clearErrors(); setForm(p => ({ ...p, orphans: p.orphans.filter((_, i) => i !== idx) })); };

  const setOrphanField = (idx, field, v) => {
    clearErrors();
    const updatedOrphans = form.orphans.map((o, i) => i === idx ? { ...o, [field]: v } : o);
    let updatedForm = { ...form, orphans: updatedOrphans };
    if (form.hasSpouse && field === 'orphanRate') {
      const totalOrphanRate = updatedOrphans.reduce((sum, o) => sum + (parseFloat(o.orphanRate) || 0), 0);
      const currentSpouse = Number(form.spousePensionRate) || 0;
      if (currentSpouse + totalOrphanRate > 100) {
        const validSpouseOpts = [30, 40, 50, 60, 70, 80, 90, 100].filter(v => v + totalOrphanRate <= 100);
        updatedForm = { ...updatedForm, spousePensionRate: validSpouseOpts.length > 0 ? validSpouseOpts[validSpouseOpts.length - 1] : 0 };
      }
    }
    setForm(updatedForm);
  };

  const hasError = (fieldName) => bannerErrors.some(e => e.field === fieldName);

  // Orphan rate warnings — shown inline, do NOT block form submission
  const orphanRateSum = form.orphans.reduce((sum, c) => sum + (Number(c.orphanRate) || 0), 0);

  const _rawPm = Number(form.retirementMonth);
  const _rawPy = Number(form.retirementYear);
  const pensionMonthForOrphan = _rawPm === 12 ? 1 : _rawPm + 1;
  const pensionYearForOrphan  = _rawPm === 12 ? _rawPy + 1 : _rawPy;
  const eligibleZeroRateCount = form.orphans.filter(c => {
    if (!c.birthdate) return false;
    const [by, bm] = c.birthdate.split('-').map(Number);
    const ageMonths = (pensionYearForOrphan - by) * 12 + (pensionMonthForOrphan - bm);
    return ageMonths < 252 && !(Number(c.orphanRate) > 0);
  }).length;

  const orphanWarning = form.orphans.length > 0
    ? orphanRateSum > 100
      ? t('orphanRateTooHigh')
      : (form.hasSpouse && orphanRateSum > 0 && Number(form.spousePensionRate) < 30)
        ? t('orphanRateTooLow')
        : eligibleZeroRateCount > 0
          ? eligibleZeroRateCount === 1
            ? t('orphanEligibleNoRate')
            : t('orphansEligibleNoRate')
          : null
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    const errs = [];
    if (!form.savingsAmount || Number(form.savingsAmount) <= 0)
      errs.push({ field: 'savingsAmount', message: `${t('savingsAmount')} — ${t('errorSavings')}` });
    if (!form.memberGender)
      errs.push({ field: 'memberGender', message: `${t('memberGender')} — ${t('errorRequired')}` });
    if (!form.memberBirthDate)
      errs.push({ field: 'memberBirthDate', message: `${t('memberBirthDate')} — ${t('errorRequired')}` });
    if (form.hasSpouse && !form.spouseBirthDate)
      errs.push({ field: 'spouseBirthDate', message: `${t('spouseBirthDate')} — ${t('errorRequired')}` });
    form.orphans.forEach((child, idx) => {
      if (!child.birthdate)
        errs.push({ field: `orphans[${idx}].birthdate`, message: `${t('orphanBirthDate', { n: idx + 1 })} — ${t('errorRequired')}` });
      if (!(Number(child.orphanRate) > 0)) {
        const eligible = !child.birthdate || (() => {
          const [by, bm] = child.birthdate.split('-').map(Number);
          return (pensionYearForOrphan - by) * 12 + (pensionMonthForOrphan - bm) < 252;
        })();
        if (eligible)
          errs.push({ field: `orphans[${idx}].orphanRate`, message: `שיעור ילד ${idx + 1} (%) — ${t('errorRequired')}` });
      }
    });

    if (errs.length > 0) { setBannerErrors(errs); return; }
    setBannerErrors([]);
    onLoading(true);

    const rawMonth = Number(form.retirementMonth);
    const rawYear  = Number(form.retirementYear);
    const pensionMonth = rawMonth === 12 ? 1  : rawMonth + 1;
    const pensionYear  = rawMonth === 12 ? rawYear + 1 : rawYear;

    const payload = {
      fundType:            form.fundType,
      retirementMonth:     pensionMonth,
      retirementYear:      pensionYear,
      memberBirthDate:     form.memberBirthDate,
      memberGender:        form.memberGender,
      hasSpouse:           form.hasSpouse,
      spouseBirthDate:     form.hasSpouse ? form.spouseBirthDate   : undefined,
      spouseGender:        form.hasSpouse ? form.spouseGender      : undefined,
      spousePensionRate:   form.hasSpouse ? Number(form.spousePensionRate) / 100 : 0,
      savingsAmount:       parseNumber(String(form.savingsAmount)),
      securityPeriod:      Number(form.securityPeriod),
      advanceMonths:       Number(form.advanceMonths),
      capitalizationRate:  Number(form.capitalizationRate) / 100,
      capitalizationYears: Number(form.capitalizationYears),
      managementFee:       Number(form.managementFee) / 100,
      insuranceEndAge:     Number(form.insuranceEndAge),
      orphans:             form.orphans
        .filter(c => c.birthdate)
        .map(c => ({ birthdate: c.birthdate, orphanRate: Number(c.orphanRate) || 0 })),
    };
    try {
      const debugParam = config.testingMode ? '?debug=true' : '';
      const res = await fetch(`${API_BASE}/api/calculate${debugParam}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        if (res.status === 400 && errBody.error === 'validation_error') {
          const getFieldName = (field) => {
            if (FIELD_KEY_MAP[field]) return t(FIELD_KEY_MAP[field]);
            const rateMatch = field.match(/^orphans\[(\d+)\]\.orphanRate$/);
            if (rateMatch) return t('orphanRatePerChild') + ` ${parseInt(rateMatch[1]) + 1}`;
            const dateMatch = field.match(/^orphans\[(\d+)\]\.birthdate$/);
            if (dateMatch) return t('orphanBirthDate', { n: parseInt(dateMatch[1]) + 1 });
            return field;
          };
          const getReason = (reason) => {
            const key = REASON_KEY_MAP[reason];
            return key ? t(key) : reason;
          };
          const apiErrs = [
            ...(errBody.missingFields || []).map(f => ({ field: f, message: `${getFieldName(f)} — ${t('errorRequired')}` })),
            ...(errBody.invalidFields || []).map(e => ({ field: e.field, message: `${getFieldName(e.field)} — ${getReason(e.reason)}` })),
          ];
          setBannerErrors(apiErrs);
          return;
        }
        console.error('[ERROR] API returned', res.status, errBody);
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      console.log('[DEBUG] API response:', data);
      if (debugMode) {
        setDebugResult({ debug: data.debug });
      }
      onResult(data, form);
      setBannerWarning(orphanWarning || null);
    } catch {
      onResult(null, form);
    } finally {
      onLoading(false);
    }
  };

  const handleReset = () => { setForm(defaultForm); clearErrors(); setDebugResult(null); setIdWarning(false); onResult(null, null); };

  const hasSpouse = form.hasSpouse;
  const _pm = Number(form.retirementMonth);
  const _py = Number(form.retirementYear);

  const retirementAge = (() => {
    if (!form.memberBirthDate) return null;
    const [birthYear, birthMonth] = form.memberBirthDate.split('-').map(Number);
    const ageMonths = (_py - birthYear) * 12 + (_pm - birthMonth);
    return Math.floor(ageMonths / 12);
  })();

  const retirementAgeAlert = (() => {
    if (retirementAge === null) return null;
    if (retirementAge < 60) return { severity: 'error', key: 'errorAge', block: true };
    return null;
  })();

  const CURRENT_YEAR_NOW  = new Date().getFullYear();
  const CURRENT_MONTH_NOW = new Date().getMonth() + 1;
  const retirementTotalMonths = Number(form.retirementYear) * 12 + Number(form.retirementMonth);
  const currentTotalMonths    = CURRENT_YEAR_NOW * 12 + CURRENT_MONTH_NOW;
  const isFutureRetirement    = retirementTotalMonths > currentTotalMonths + 1;

  const pensionPaymentDateStr = `01/${String(_pm === 12 ? 1 : _pm + 1).padStart(2, '0')}/${_pm === 12 ? _py + 1 : _py}`;

  const tbSx = {
    flex: 1,
    fontSize: '12px',
    fontWeight: 600,
    color: '#5a6a7a',
    border: 'none',
    '&.Mui-selected': {
      backgroundColor: config.ctaColor,
      color: '#fff',
      '&:hover': { backgroundColor: config.ctaHoverColor },
    },
  };

  // NOTE: RadioToggle and FL are declared at module scope (above this component).
  // Declaring them here gave them a new component identity on every render, which
  // remounted the radios, dropped focus to <body>, and made NVDA read the page title.

  // card styling matching design-v13.html exactly
  const cardSx = { borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.07)' };

  // field styling matching HTML exactly
  const fSx = {
    '& .MuiInputBase-root': { height: '36px', fontSize: '12.5px', borderRadius: '7px', background: '#fafcfe' },
    '& .MuiInputBase-input': { fontSize: '12.5px', py: 0 },
    '& .MuiSelect-select': { py: '7px', fontSize: '12.5px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.5)', borderWidth: '1.5px' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080', borderWidth: '1.5px' },
  };
  // children fields: 30px height, 12px font
  const fSmSx = {
    '& .MuiInputBase-root': { height: '30px', fontSize: '12px', borderRadius: '7px', background: '#fafcfe' },
    '& .MuiInputBase-input': { fontSize: '12px', py: 0 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.5)', borderWidth: '1.5px' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' },
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

        {/* ── Validation Error Banner ── */}
        {bannerErrors.length > 0 && (
          <Box ref={errorBannerRef} tabIndex={-1} role="alert" aria-live="assertive" aria-atomic="true"
            sx={{ background: '#fcebeb', border: '1px solid #f09595', borderRadius: '8px', p: '12px 16px', outline: 'none' }}>
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Box component="span" aria-hidden="true" sx={{ color: '#e24b4a', fontSize: '18px', lineHeight: 1 }}>⚠</Box>
              <Box>
                <Typography sx={{ fontSize: '13px', color: '#5a0000', fontWeight: 600, mb: '5px' }}>
                  {t('errorBannerTitle')}
                </Typography>
                <Box component="ul" sx={{ fontSize: '12px', color: '#5a0000', paddingRight: '16px', lineHeight: 1.8, m: 0 }}>
                  {bannerErrors.map((e, i) => <li key={i}>{e.message}</li>)}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Warning Banner (post-calculation) ── */}
        {bannerWarning && (
          <Box role="alert" aria-live="polite" sx={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', p: '10px 16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Box component="span" aria-hidden="true" sx={{ color: '#f59e0b', fontSize: '16px', lineHeight: 1 }}>⚠</Box>
            <Typography sx={{ fontSize: '12.5px', color: '#5c3d00', fontWeight: 600 }}>{bannerWarning}</Typography>
          </Box>
        )}

        {/* ── Page title + subtitle ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CalculateIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography component="h1" sx={{ fontSize: '18px', fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
              {t('appTitle')}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#5E6069', mt: 0.25 }}>
              {t('formSubtitle')}
            </Typography>
          </Box>
        </Box>

        {/* ── ROW 1: personal + merged spouse+children ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '8px' }}>

          {/* ── CARD 1: פרטים אישיים ── */}
          <Card component="section" elevation={0} sx={cardSx}>
            <CardContent sx={{ p: '10px 16px', '&:last-child': { pb: '10px' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, mb: 1, pb: 1, borderBottom: '1.5px solid #edf3f8' }}>
                <Box component="svg" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" sx={{ width: 16, height: 16, color: 'secondary.main', flexShrink: 0 }}>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </Box>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: 'secondary.main' }}>{t('cardPersonal')}</Typography>
              </Box>

              {/* Row 1 */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '9px', alignItems: { xs: 'stretch', md: 'flex-end' } }}>
                {(config.showClientName ?? true) && (
                  <Box sx={{ width: { xs: '100%', md: 195 }, flex: 'none' }}>
                    <FL htmlFor="input-clientName">{t('clientName')}</FL>
                    <TextField id="input-clientName" size="small" fullWidth sx={fSx} value={form.clientName} onChange={e => set('clientName', e.target.value)} placeholder={t('clientNamePlaceholder')} inputProps={{ 'aria-required': true }} />
                  </Box>
                )}
                {(config.showClientId ?? true) ? (
                  <Box sx={{ flex: { xs: 'none', md: 1 }, width: { xs: '100%', md: 'auto' } }}>
                    <FL htmlFor="input-clientId">{t('clientId')}</FL>
                    <TextField
                      id="input-clientId"
                      size="small" fullWidth sx={fSx} value={form.clientId}
                      onChange={e => { const val = e.target.value.replace(/\D/g, '').slice(0, 9); set('clientId', val); setIdWarning(false); }}
                      onBlur={() => { if (form.clientId.length > 0 && form.clientId.length !== 9) setIdWarning(true); }}
                      placeholder={t('clientIdPlaceholder')}
                      inputProps={{ inputMode: 'numeric', maxLength: 9 }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ flex: { xs: 'none', md: 1 } }} />
                )}
                <Box sx={{ width: { xs: '100%', md: 148 }, flex: 'none' }}>
                  <FL htmlFor="input-memberBirthDate">{t('memberBirthDate')}</FL>
                  <Box dir="ltr">
                  <TextField id="input-memberBirthDate" size="small" fullWidth sx={fSx} type="text"
                    value={getDateDisplay('memberBirthDate', form.memberBirthDate)}
                    placeholder="DD/MM/YYYY"
                    onChange={e => handleDateChange('memberBirthDate', e)}
                    error={hasError('memberBirthDate')}
                    inputProps={{ maxLength: 10, 'aria-required': true }}
                    onFocus={e => e.target.setSelectionRange(0, 0)}
                  />
                  </Box>
                </Box>
              </Box>
              {idWarning && <Alert severity="warning" sx={{ py: 0, mt: 0.5 }}>{t('errorClientIdLength')}</Alert>}

              {/* Row 2 */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '9px', alignItems: { xs: 'stretch', md: 'flex-end' }, mt: '9px' }}>
                <Box sx={{ width: { xs: '100%', md: 195 }, flex: 'none' }}>
                  <FL>{t('memberGender')}</FL>
                  <RadioToggle
                    name="memberGender" value={form.memberGender}
                    ariaLabel={t('memberGender')}
                    onChange={val => setForm(prev => ({ ...prev, memberGender: val, spouseGender: val === 'male' ? 'female' : 'male' }))}
                    options={[{ val: 'male', label: t('genderMale') }, { val: 'female', label: t('genderFemale') }]}
                  />
                </Box>
                <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' } }} />
                <Box sx={{ width: { xs: '100%', md: 148 }, flex: 'none' }}>
                  <FL htmlFor="input-retirementAge">{t('retirementAge')}</FL>
                  <TextField id="input-retirementAge" size="small" fullWidth value={retirementAge !== null ? retirementAge : ''} InputProps={{ readOnly: true }}
                    inputProps={{ 'aria-readonly': true }}
                    sx={{ ...fSx, '& .MuiInputBase-root': { ...fSx['& .MuiInputBase-root'], background: '#f0fafa' }, '& .MuiInputBase-input': { fontSize: '12.5px', color: '#1A2C5B', textAlign: 'center', fontWeight: 700, py: 0 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#008080', borderWidth: '1.5px' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }}
                  />
                </Box>
              </Box>
              {retirementAgeAlert && (
                <Alert severity={retirementAgeAlert.severity} sx={{ mt: 1, py: 0 }}>
                  {t(retirementAgeAlert.key)}
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* ── CARD 2: בן / בת זוג וילדים ── */}
          <Card component="section" elevation={0} sx={cardSx}>
            <CardContent sx={{ p: '10px 16px', '&:last-child': { pb: '10px' } }}>

              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: '10px', pb: '8px', borderBottom: '1.5px solid #edf3f8' }}>
                <Box component="svg" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" sx={{ width: 16, height: 16, color: 'secondary.main', flexShrink: 0 }}>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </Box>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: 'secondary.main' }}>בן / בת זוג וילדים</Typography>
              </Box>

              {/* ── SPOUSE SECTION — fixed widths with spacer ── */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '9px', alignItems: { xs: 'stretch', md: 'flex-end' } }}>
                <Box sx={{ width: { xs: '100%', md: 195 }, flex: 'none' }}>
                  <FL>{t('hasSpouse')}</FL>
                  <RadioToggle
                    name="hasSpouse" value={hasSpouse ? 'yes' : 'no'}
                    ariaLabel={t('hasSpouse')}
                    onChange={val => set('hasSpouse', val === 'yes')}
                    options={[{ val: 'yes', label: t('hasSpouseYes') }, { val: 'no', label: t('hasSpouseNo') }]}
                  />
                </Box>
                <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' } }} />
                <Box sx={{ width: { xs: '100%', md: 148 }, flex: 'none', display: hasSpouse ? undefined : 'none' }} aria-hidden={!hasSpouse}>
                    <FL htmlFor="input-spouseBirthDate">{t('spouseBirthDate')}</FL>
                    <Box dir="ltr">
                    <TextField
                      id="input-spouseBirthDate"
                      size="small" fullWidth sx={fSx} type="text"
                      value={getDateDisplay('spouseBirthDate', form.spouseBirthDate)}
                      placeholder="DD/MM/YYYY"
                      onChange={e => handleDateChange('spouseBirthDate', e)}
                      error={hasError('spouseBirthDate')}
                      inputProps={{ maxLength: 10, 'aria-required': true }}
                      onFocus={e => e.target.setSelectionRange(0, 0)}
                    />
                    </Box>
                  </Box>
              </Box>

              <Box sx={{ display: hasSpouse ? 'flex' : 'none', flexDirection: { xs: 'column', md: 'row' }, gap: '9px', alignItems: { xs: 'stretch', md: 'flex-end' }, mt: '9px' }} aria-hidden={!hasSpouse}>
                  <Box sx={{ width: { xs: '100%', md: 195 }, flex: 'none' }}>
                    <FL>{t('spouseGender')}</FL>
                    <RadioToggle
                      name="spouseGender" value={form.spouseGender}
                      ariaLabel={t('spouseGender')}
                      onChange={val => set('spouseGender', val)}
                      options={[{ val: 'male', label: t('genderMale') }, { val: 'female', label: t('genderFemale') }]}
                    />
                  </Box>
                  <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' } }} />
                  <Box sx={{ width: { xs: '100%', md: 148 }, flex: 'none' }}>
                    <FL>{t('spousePensionRate')}</FL>
                    <TextField size="small" fullWidth sx={fSx} select
                      value={form.spousePensionRate}
                      onChange={e => set('spousePensionRate', e.target.value)}
                      slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('spousePensionRate') } } }}
                    >
                      {[30, 40, 50, 60, 70, 80, 90, 100]
                        .filter(v => v + form.orphans.reduce((s, o) => s + (Number(o.orphanRate) || 0), 0) <= 100)
                        .map(v => <MenuItem key={v} value={v}>{v}%</MenuItem>)}
                    </TextField>
                  </Box>
                </Box>

              {/* ── CHILDREN SECTION ── */}
              <Box sx={{ mt: '12px', pt: '10px', borderTop: '1px solid #edf3f8' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: form.orphans.length > 0 ? '8px' : 0 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#5E6069', textTransform: 'uppercase', letterSpacing: '.4px' }}>{t('cardChildren')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#5E6069', textTransform: 'uppercase', letterSpacing: '.4px' }}>{t('numChildren')}</Typography>
                    <TextField select size="small" value={form.orphans.length}
                      slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('numChildren') } } }}
                      onChange={e => {
                        const target = Number(e.target.value);
                        const current = form.orphans.length;
                        if (target > current) {
                          let f = form;
                          for (let i = current; i < target; i++) {
                            const updatedOrphans = [...f.orphans, { birthdate: '', orphanRate: '' }];
                            f = { ...f, orphans: updatedOrphans };
                            if (f.hasSpouse) {
                              const totalOrphanRate = updatedOrphans.reduce((sum, o) => sum + (parseFloat(o.orphanRate) || 0), 0);
                              const maxSpouse = Math.max(0, 100 - totalOrphanRate);
                              if ((parseFloat(f.spousePensionRate) || 0) > maxSpouse) f = { ...f, spousePensionRate: maxSpouse };
                            }
                          }
                          clearErrors(); setForm(f);
                        } else if (target < current) {
                          clearErrors(); setForm(p => ({ ...p, orphans: p.orphans.slice(0, target) }));
                        }
                      }}
                      sx={{ width: 60,
                        '& .MuiInputBase-root': { height: '28px', borderRadius: '6px', background: '#ffffff', fontSize: '12px', fontWeight: 700, color: '#008080' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.5)', borderWidth: '1.5px' },
                        '& .MuiSelect-select': { py: '4px', fontSize: '12px', fontWeight: 700, color: '#008080' },
                      }}
                    >
                      {[0, 1, 2, 3].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </TextField>
                  </Box>
                </Box>

                {form.orphans.map((child, idx) => (
                  <Box key={idx} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '9px', py: '5px', borderBottom: idx < form.orphans.length - 1 ? '1px solid #edf3f8' : 'none' }}>
                    <Box sx={{ width: { xs: '45%', md: 148 }, flex: 'none' }}>
                      <FL htmlFor={`input-orphan-date-${idx}`}>{t('orphanBirthDate', { n: idx + 1 })}</FL>
                      <Box dir="ltr">
                        <TextField id={`input-orphan-date-${idx}`} size="small" fullWidth sx={fSmSx} type="text"
                          value={getDateDisplay(`orphan_${idx}`, child.birthdate)} placeholder="DD/MM/YYYY"
                          onChange={e => handleOrphanDateChange(idx, e)}
                          error={hasError(`orphans[${idx}].birthdate`)}
                          inputProps={{ maxLength: 10 }}
                          onFocus={e => e.target.setSelectionRange(0, 0)}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ width: { xs: '35%', md: 105 }, flex: 'none' }}>
                      <FL>{`${t('orphanRatePerChild')} ילד ${idx + 1}`}</FL>
                      <TextField size="small" fullWidth sx={fSmSx} select
                        value={child.orphanRate}
                        onChange={e => setOrphanField(idx, 'orphanRate', e.target.value)}
                        error={hasError(`orphans[${idx}].orphanRate`)}
                        slotProps={{ select: { SelectDisplayProps: { 'aria-label': `${t('orphanRatePerChild')} ילד ${idx + 1}` } } }}
                      >
                        {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
                          .filter(v => {
                            const othersSum = form.orphans.reduce((s, o, i) => i === idx ? s : s + (Number(o.orphanRate) || 0), 0);
                            return othersSum + v <= 100;
                          })
                          .map(v => <MenuItem key={v} value={v}>{v}%</MenuItem>)}
                      </TextField>
                    </Box>
                    <IconButton onClick={() => removeOrphan(idx)} size="small"
                      aria-label={`${t('removeOrphan')} ילד ${idx + 1}`}
                      sx={{ width: 26, height: 26, mb: '2px', border: '1.5px solid rgba(0,0,0,0.5)', background: '#f0fafa', color: '#008080', '&:hover': { background: '#e0f5f5' } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} aria-hidden="true" />
                    </IconButton>
                  </Box>
                ))}
              </Box>

            </CardContent>
          </Card>

        </Box>
        {/* END ROW 1 */}

        {/* ── ROW 2: retirement + financial ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '8px' }}>

          {/* ── CARD 3: פרטי פרישה ── */}
          <Card component="section" elevation={0} sx={{ ...cardSx, overflow: 'visible' }}>
            <CardContent sx={{ p: '10px 16px', '&:last-child': { pb: '10px' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, mb: 1, pb: 1, borderBottom: '1.5px solid #edf3f8' }}>
                <Box component="svg" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" sx={{ width: 16, height: 16, color: 'secondary.main', flexShrink: 0 }}>
                  <path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7"/>
                </Box>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: 'secondary.main' }}>{t('cardRetirement')}</Typography>
              </Box>

              {/* Row 1 */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '9px', alignItems: 'flex-end' }}>
                <Box sx={{ width: { xs: '100%', md: 162 }, flex: 'none' }}>
                  <FL>{t('fundType')}</FL>
                  <TextField size="small" fullWidth sx={fSx} select value={form.fundType} onChange={e => set('fundType', e.target.value)} slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('fundType') } } }}>
                    <MenuItem value="comprehensive">{t('fundTypeComprehensive')}</MenuItem>
                    <MenuItem value="supplementary">{t('fundTypeSupplementary')}</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' } }} />
                <Box sx={{ width: 90, flex: 'none' }}>
                  <FL>{t('retirementMonth')}</FL>
                  <TextField size="small" fullWidth sx={fSx} select value={form.retirementMonth} onChange={e => set('retirementMonth', e.target.value)} slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('retirementMonth') } } }}>
                    {MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </TextField>
                </Box>
                <Box sx={{ width: 88, flex: 'none' }}>
                  <FL>{t('retirementYear')}</FL>
                  <YearPickerAccordion
                    value={form.retirementYear}
                    onChange={val => set('retirementYear', val)}
                    minYear={MIN_RETIREMENT_YEAR}
                    maxYear={form.memberBirthDate ? parseInt(form.memberBirthDate.split('-')[0]) + 85 : CURRENT_YEAR + 20}
                    aria-label={t('retirementYear')}
                    sx={{ width: 88 }}
                  />
                </Box>
              </Box>

              {/* Row 2 */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '9px', alignItems: { xs: 'stretch', md: 'flex-end' }, mt: '9px' }}>
                {(config.showInsuranceEndAge ?? true) && (
                  <Box sx={{ width: { xs: '100%', md: 162 }, flex: 'none' }}>
                    <FL>{t('insuranceEndAge')}</FL>
                    <TextField size="small" fullWidth sx={fSx} select value={form.insuranceEndAge} onChange={e => set('insuranceEndAge', e.target.value)} slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('insuranceEndAge') } } }}>
                      {INSURANCE_END_AGE_OPTS.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </TextField>
                  </Box>
                )}
                <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' } }} />
                <Box sx={{ width: { xs: '100%', md: 187 }, flex: 'none' }}>
                  <FL htmlFor="input-pensionPaymentDate">{t('pensionPaymentDate')}</FL>
                  <TextField id="input-pensionPaymentDate" size="small" fullWidth value={pensionPaymentDateStr} InputProps={{ readOnly: true }}
                    inputProps={{ 'aria-readonly': true }}
                    sx={{ ...fSx, '& .MuiInputBase-root': { ...fSx['& .MuiInputBase-root'], background: '#f0fafa' }, '& .MuiInputBase-input': { fontSize: '12.5px', color: '#1A2C5B', textAlign: 'center', fontWeight: 700, py: 0 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#008080', borderWidth: '1.5px' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }}
                  />
                </Box>
              </Box>
              {isFutureRetirement && (
                <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                  {t('futureRetirementNote')}
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* ── CARD 4: נתונים פיננסיים ── */}
          <Card component="section" elevation={0} sx={cardSx}>
            <CardContent sx={{ p: '10px 16px', '&:last-child': { pb: '10px' } }}>

              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, mb: 1, pb: 1, borderBottom: '1.5px solid #edf3f8' }}>
                <Box component="svg" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="#008080" strokeWidth="1.8" sx={{ width: 16, height: 16, flexShrink: 0 }}>
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                  <line x1="2" y1="20" x2="22" y2="20"/>
                </Box>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#008080' }}>{t('financialParams')}</Typography>
              </Box>

              {/* Savings — large input */}
              <Box sx={{ mb: '10px' }}>
                <FL htmlFor="input-savingsAmount">{t('savingsLabel')}</FL>
                <Box
                  component="input"
                  id="input-savingsAmount"
                  type="text"
                  aria-required="true"
                  value={formatNumber(form.savingsAmount)}
                  onChange={e => { const raw = e.target.value.replace(/,/g, ''); if (raw === '' || /^\d*$/.test(raw)) set('savingsAmount', raw); }}
                  inputMode="numeric"
                  data-testid="savings-input"
                  sx={{
                    width: '100%', height: '46px', border: '1.5px solid rgba(0,0,0,0.5)', borderRadius: '8px',
                    color: '#008080', fontWeight: 900, fontSize: { xs: '22px', md: '28px' }, textAlign: 'center',
                    direction: 'ltr', background: '#fff', outline: 'none', padding: '0 12px',
                    fontFamily: 'inherit', cursor: 'text',
                    '&:hover': { borderColor: '#008080' },
                    '&:focus': { borderColor: '#008080' },
                    '&:focus-visible': { outline: '2px solid #008080', outlineOffset: '-2px' },
                  }}
                />
              </Box>

              {/* Parameters — 3-column grid for consistent alignment */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', alignItems: 'flex-end' }}>
                <Box>
                  <FL>{t('securityPeriod')}</FL>
                  <TextField size="small" fullWidth sx={fSx} select value={form.securityPeriod} onChange={e => set('securityPeriod', e.target.value)} slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('securityPeriod') } } }}>
                    <MenuItem value={0}>{t('secPeriodNone')}</MenuItem>
                    {[60, 120, 180, 240].map(n => <MenuItem key={n} value={n}>{t('secPeriodMonths', { n })}</MenuItem>)}
                  </TextField>
                </Box>
                <Box>
                  <FL>{t('advanceMonths')}</FL>
                  <TextField size="small" fullWidth sx={fSx} select value={form.advanceMonths} onChange={e => set('advanceMonths', e.target.value)} slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('advanceMonths') } } }}>
                    {ADVANCE_MONTH_OPTS.map(v => <MenuItem key={v} value={v}>{v === 0 ? '0' : t('advanceMonthsOption', { n: v })}</MenuItem>)}
                  </TextField>
                </Box>
                <Box>
                  <FL htmlFor="input-managementFee">{t('managementFee')}</FL>
                  <TextField id="input-managementFee" size="small" fullWidth value={`${Number(form.managementFee).toFixed(2)}%`} InputProps={{ readOnly: true }}
                    inputProps={{ 'aria-readonly': true }}
                    sx={{ ...fSx, '& .MuiInputBase-root': { ...fSx['& .MuiInputBase-root'], background: '#f0fafa' }, '& .MuiInputBase-input': { fontSize: '12.5px', color: '#1A2C5B', textAlign: 'center', fontWeight: 700, py: 0 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#008080', borderWidth: '1.5px' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }}
                  />
                </Box>
                <Box>
                  <FL>{t('capYears')}</FL>
                  <TextField size="small" fullWidth sx={fSx} select value={form.capitalizationYears}
                    slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('capYears') } } }}
                    onChange={e => { const val = Number(e.target.value); set('capitalizationYears', val); if (val > 0 && Number(form.capitalizationRate) === 0) set('capitalizationRate', 25); if (val === 0) set('capitalizationRate', 0); }}
                  >
                    {[0, ...CAPITALIZATION_YEARS_OPTS].map(v => <MenuItem key={v} value={v}>{v === 0 ? t('secPeriodNone') : `${v} ${t('years')}`}</MenuItem>)}
                  </TextField>
                </Box>
                <Box>
                  <FL>{t('capRate')}</FL>
                  <TextField size="small" fullWidth sx={fSx} select value={form.capitalizationRate} disabled={form.capitalizationYears === 0} onChange={e => set('capitalizationRate', Number(e.target.value))} slotProps={{ select: { SelectDisplayProps: { 'aria-label': t('capRate') } } }}>
                    {CAPITALIZATION_RATE_OPTS.map(v => <MenuItem key={v} value={v}>{v}%</MenuItem>)}
                  </TextField>
                </Box>
              </Box>

            </CardContent>
          </Card>

        </Box>
        {/* END ROW 2 */}

        {/* ── ACTION BUTTONS — איפוס שמאל, חישוב ימין (ב-RTL: grid LTR) ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

          {/* איפוס — שמאל (ראשון ב-LTR grid) */}
          <Button type="button" variant="outlined" color="primary" fullWidth onClick={handleReset}
            sx={{ height: '48px', borderRadius: '8px', fontSize: '13.5px', fontWeight: 600, color: '#607080', borderColor: 'rgba(0,0,0,0.5)', gap: '8px' }}>
            <Box component="svg" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" sx={{ width: 16, height: 16, flexShrink: 0 }}>
              <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.85"/>
            </Box>
            {t('reset')}
          </Button>

          {/* חישוב — ימין (שני ב-LTR grid) */}
          <Button type="submit" variant="contained" color="secondary" fullWidth
            disabled={retirementAgeAlert?.block === true}
            sx={{ height: '48px', borderRadius: '8px', fontWeight: 700, fontSize: '15px', boxShadow: '0 3px 10px rgba(0,178,178,.25)', gap: '8px' }}
            data-testid="calculate-button">
            <Box component="svg" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" sx={{ width: 16, height: 16, flexShrink: 0 }}>
              <rect x="4" y="2" width="16" height="20" rx="2"/>
              <line x1="8" y1="7" x2="16" y2="7"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
              <line x1="8" y1="17" x2="12" y2="17"/>
            </Box>
            {t('calculate')}
          </Button>

        </Box>

      </Box>{/* end form */}

      {debugMode && (
        <div style={{
          position: 'fixed',
          left: 0,
          top: '80px',
          width: '460px',
          zIndex: 1000,
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '0 8px 8px 0',
          boxShadow: '2px 2px 8px rgba(0,0,0,0.1)',
          padding: '16px',
          direction: 'rtl',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 100px)'
        }}>
          <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px' }}>
            {t('debugScenarios')}
          </div>
          <select
            onChange={(e) => {
              const i = parseInt(e.target.value);
              if (!isNaN(i)) loadScenario(scenarios[i]);
            }}
            style={{ width: '100%', fontSize: '12px', padding: '6px',
                     borderRadius: '6px', marginBottom: '16px' }}
            defaultValue=""
          >
            <option value="">{t('debugSelectScenario')}</option>
            {scenarios.map((s, i) => (
              <option key={i} value={i}>{s.name}</option>
            ))}
          </select>

          {debugResult && <DebugPanel debug={debugResult.debug} />}
        </div>
      )}
    </>
  );
}
